import { useState, Fragment, useEffect } from 'react';
import { TextField, Autocomplete, Paper, IconButton, Divider, createFilterOptions, FormHelperText } from '@mui/material';
import styled from '@emotion/styled';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import { useField, Field, useFormikContext } from 'formik';

import { get, update } from 'lodash';

import Dialog from '../utilities/Dialog';

// context of additional state
import { useFormData } from '../../contexts/FormData';

// hooks
import useInputDebounce from '../../hooks/useInputDebounce';

import ErrorMessage from '../MuiFields/ErrorMessage';


const CustomAutocomplete = styled(Autocomplete)`
 
`;

const filter = createFilterOptions();

const updateFields = (option, sfv, formUpdates) => {
  if (sfv && formUpdates?.length) {
    formUpdates.forEach(el => {
      sfv(el.name, option ? get(option, el.field) : '');
    });
  }
}

/**
 * AutoCompletSolo - A custom autocomplete component with free solo input
 * 
 * sample usage: const options = useInputDebounce({ url: 'inventory_items', state: true });
 * @optional = displayLabel = (option) => `${option.name} - ${option.title}`
 * <AutoCompleteSolo label="Stylist" fieldName="stylist_id" newItemTitle="Stylist" {...options} />
 * 
 * @required setValue {Function} - State setter for input value
 * @required value {string} - Current input value
 * @required fieldName {string} - Option object property to use for values
 * 
 * @optional handleInputChange {Function} - Callback on input change
 * @optional loading {boolean} - Loading state (default: true)
 * @optional options {Array} - Dropdown options (default: [{name: "hello", title: "hello"}])
 * @optional popup {ReactNode} - Custom popup icon (default: null)
 * @optional newItemTitle {string} - Text for "Create new" option (default: "Item")
 * @optional displayLabel {Function} - Custom label formatter
 * @optional ...props - Additional CustomAutocomplete props
 */

const AutoCompleteSolo = ({fieldName: optionField, popup = null, newItemTitle = "Item", displayLabel, size = "small", placeholder = "Search", useForm = false, FormComponent, update, serverOptions, displayField, setDisplayField, url, style, select, formUpdates, server = true, helperText, conditionStr, simple, forceServer = false, ...props}) => {

  const { formValues, setFormValues } = useFormData() || {};

  const { setFieldValue } = useFormikContext();

  let renderLabel = displayField || optionField;

  const { handleInputChange, loading, options, value: inputValue, setValue } = useInputDebounce({ url, state: true, select, serverOptions, conditionStr, forceServer });

  const [field, meta, helpers] = useField(props);

  const { onBlur, onChange, name, value } = field;

  const [openDialog, setOpenDialog] = useState(false)

  const handleChangeInput = (e, val, reason) => {
    let change = ["input", "clear"].includes(reason) ? true : false;
    if (change) {
      setValue(val);
      if (handleInputChange) {
        handleInputChange(val, e);
      }
    }
  }

  console.log(inputValue, "inputValue");

  const handleBlur = async (e) => {
    let findOption = options?.find(el => el[optionField]?.toString() === inputValue?.toString());
    if (!value || !findOption) {
      if (simple && inputValue) {
        // Use await to ensure the value is set in Formik state
        await helpers.setValue(inputValue);
        updateFields(null, setFieldValue, formUpdates);
        // Manually trigger validation after the value is set
        helpers.setTouched(true);
      } else {
        if (simple) setValue('');
      }
    }
    // Only call the original onBlur if we didn't just manually set a value,
    // or call it after the update to ensure Formik sees the new state.
    onBlur(e);
  }

  useEffect(() => {
    if (!value) {
      setValue('');
      console.log('removed updateFields');
      // removed updateFields, only updates with direct action
    }

    let findOption = options?.find(el => el[optionField]?.toString() === value?.toString());

    if (!findOption) {
      if (simple) {
        setValue(value);
        return;
      } else {
        return; // in case of true solo, with no ability to create new option
      }
    } 
    setTimeout(() => {
      setValue(findOption ? findOption[renderLabel] : '');
      updateFields(findOption, setFieldValue, formUpdates);
    }, 0);
  }, [value, options])

  useEffect(() => {
    if (serverOptions?.length) {
      let findOption = options?.find(el => el[optionField]?.toString() === value?.toString());

      if (!findOption) {
        if (simple) {
          setValue(value);
          return;
        } else {
          return; // in case of true solo, with no ability to create new option
        }
      } 
      setTimeout(() => {
        setValue(findOption ? findOption[renderLabel] : '');
        updateFields(findOption, setFieldValue, formUpdates);
      }, 0);
    }
  }, [serverOptions, options])

  const handleChange = (e, newValue) => {
   // Update the Formik field value with the selected option
    if (newValue) {
      // Update Formik field with the complete object
      helpers.setValue(newValue[optionField]?.toString());
    } else {
      // Clear both the field value and input text when selection is cleared
      helpers.setValue(null);
      updateFields(null, setFieldValue, formUpdates);
    }
  };

  return (
    <Fragment>
      <CustomAutocomplete
        loading={loading}
        popupIcon={popup}
        options={options}
        size={size}
        onInputChange={handleChangeInput} 
        getOptionLabel={(option) => {
          return option[renderLabel]?.toString() || ""
        }}
        {...(server && !serverOptions?.length && { filterOptions: (x) => x })}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          let label;
          if (displayLabel) {
            label = displayLabel(option);
          } else {
            label = option[renderLabel];
          }
          return (
            <li key={key} {...optionProps}>
              {label}
            </li>
          );
        }}
        // isOptionEqualToValue={(option, value) => {
        //   return option.inputValue === value.inputValue || option[optionField] === value[optionField];
        // }}
        onChange={(e, newValue) => handleChange(e, newValue)}
        inputValue={inputValue || ""}
        defaultValue={''}
        fullWidth
        freeSolo
        clearOnBlur
        selectOnFocus
        onBlur={handleBlur}
        {...props}
        renderInput={(params) => {
          return (<TextField {...params} name={props.name} placeholder={placeholder} variant="outlined" style={style}  />)}
        }
      />
      {meta.touched && meta.error ? <ErrorMessage error={true} message={meta.error} /> : helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </Fragment>
  )
};

export default AutoCompleteSolo;